export type RuleType =
    | 'EmptyBusinessRule'
    | 'MinMaxBusinessRule'
    | 'EqualorGreaterThanBusinessRule'
    | 'PlaceholderBusinessRule'
    | 'MustHaveTranslationForAllCountriesBeingSoldInBusinessRule'
    | 'MustHaveTranslationForSelectedTouchPointsBusinessRule'
    | 'CollectionMinMaxBusinessRule'
    | 'GenericBusinessRule'
    | 'NumericEquationBusinessRule'
    | 'AITextValidationBusinessRule'
    | 'JevTextValidationBusinessRule';

export type RuleSeverity = 'Error' | 'Warning' | 'Info';

export interface BusinessDomain {
    id: number;
    name?: string;
}

export interface PimFieldReference {
    fieldId: number;
    fieldKey?: string;
    entity?: string;
    includeLocalizedValues?: boolean;
}

export interface NumericEquationNode {
    nodeType: 'FieldReference' | 'NumericLiteral' | 'BinaryOperation' | 'UnaryOperation' | 'Group';
    field?: PimFieldReference;
    value?: number;
    operator?: 'Add' | 'Subtract' | 'Multiply' | 'Divide' | 'Power' | 'Negate';
    left?: NumericEquationNode;
    right?: NumericEquationNode;
    operand?: NumericEquationNode;
    expression?: NumericEquationNode;
}

export interface NumericEquationExpression {
    version: number;
    root?: NumericEquationNode;
}

export interface AITextValidationConfiguration {
    version: number;
    targetFields: PimFieldReference[];
    localePolicy: 'AllAvailable' | 'ProductSalesLocales' | 'Explicit';
    locales: string[];
    prompt: string;
    contextFields: PimFieldReference[];
    missingTextPolicy: 'Skip' | 'Fail' | 'Pass';
    outputLanguage: string;
}

export interface JevTextValidationConfiguration {
    version: number;
    question: string;
    threshold: number;
    culture?: string | null;
}

export function isJevTextField(field: { datatype?: string; cvlId?: string; isMultiValue?: boolean }): boolean {
    return (field.datatype === 'String' || field.datatype === 'LocaleString') && !field.cvlId && !field.isMultiValue;
}

export function isValidJevConfiguration(configuration?: JevTextValidationConfiguration): boolean {
    return !!configuration && configuration.version === 1 && typeof configuration.question === 'string' && !!configuration.question.trim()
        && configuration.question.length <= 8000 && Number.isFinite(configuration.threshold)
        && configuration.threshold > 0 && configuration.threshold <= 1;
}

export interface PimField {
    id: number;
    key?: string;
    name?: string;
    datatype?: string;
    entity?: string;
}

export interface BusinessRuleCondition {
    operator: number;
    field?: string;
    value?: string;
}

export interface BusinessRuleValue {
    entity?: string;
    fieldName?: string;
    fieldType?: string;
    operator: number;
    valueType?: string;
    data?: unknown;
}

export interface BusinessRule {
    id: number;
    code?: string;
    entity?: string;
    field?: string;
    locale?: string | null;
    message?: string;
    rationale?: string;
    severity: RuleSeverity | number;
    type: RuleType;
    domains?: BusinessDomain[];
    conditions?: BusinessRuleCondition[];
    min?: number;
    max?: number;
    value?: BusinessRuleValue[];
    expression?: NumericEquationExpression;
    comparisonOperator?: 'Equal' | 'NotEqual' | 'GreaterThan' | 'GreaterThanOrEqual' | 'LessThan' | 'LessThanOrEqual';
    rightHandSide?: NumericEquationExpression;
    configuration?: AITextValidationConfiguration;
    jevConfiguration?: JevTextValidationConfiguration;
}

export interface RuleTypeDefinition {
    type: RuleType;
    label: string;
    description: string;
    localeStringOnly?: boolean;
    usesRange?: boolean;
    usesGenericValues?: boolean;
    usesNumericEquation?: boolean;
    usesAiTextValidation?: boolean;
    usesJevTextValidation?: boolean;
}

export const ruleTypeDefinitions: RuleTypeDefinition[] = [
    { type: 'EmptyBusinessRule', label: 'Empty field value', description: 'Validates that the selected field contains a value.' },
    { type: 'MinMaxBusinessRule', label: 'Field between min/max', description: 'Validates that a field value is between a minimum and maximum.', usesRange: true },
    { type: 'EqualorGreaterThanBusinessRule', label: 'Equal or greater than', description: 'Validates that a field value meets the minimum.', usesRange: true },
    { type: 'PlaceholderBusinessRule', label: 'Placeholder', description: 'Defines a placeholder for a hardcoded validation rule.' },
    { type: 'MustHaveTranslationForAllCountriesBeingSoldInBusinessRule', label: 'Translation sales country', description: 'Requires translations for every country where the SKU is sold.', localeStringOnly: true },
    { type: 'MustHaveTranslationForSelectedTouchPointsBusinessRule', label: 'Translation touch point', description: 'Requires translations for the selected touch points.', localeStringOnly: true },
    { type: 'CollectionMinMaxBusinessRule', label: 'Collection validation', description: 'Validates a collection count between a minimum and maximum.', usesRange: true },
    { type: 'GenericBusinessRule', label: 'Generic', description: 'Validates arbitrary field comparisons.', usesGenericValues: true },
    { type: 'NumericEquationBusinessRule', label: 'Numeric equation', description: 'Compares safe arithmetic expressions using numeric PIM fields.', usesNumericEquation: true },
    { type: 'JevTextValidationBusinessRule', label: 'Jev text validation', description: 'Checks text against a yes/no question. A yes means the text is valid.', usesJevTextValidation: true },
    { type: 'AITextValidationBusinessRule', label: 'AI text validation', description: 'Validates localized text with a server-side AI provider.', usesAiTextValidation: true },
];

export const conditionOperators = [
    'Equals', 'Begins with', 'Contains', 'Greater than or equal', 'Greater than', 'Is not null', 'Is null',
    'Less than', 'Less than or equal', 'Not equals', 'Is true', 'Is false', 'Is empty', 'Is not empty',
    'Contains all', 'Contains any', 'Not contains any', 'Not contains all', 'Not contains', 'In last months', 'In last days',
];

export function createBusinessRule(type: RuleType): BusinessRule {
    const definition = ruleTypeDefinitions.find(ruleDefinition => ruleDefinition.type === type)!;
    return {
        id: 0,
        type,
        entity: 'Item',
        severity: 'Error',
        domains: [],
        conditions: [],
        ...(definition.usesJevTextValidation ? { message: 'Text does not satisfy the configured Jev validation question.', jevConfiguration: { version: 1, question: '', threshold: 0.5, culture: null } } : {}),
        ...(definition.usesRange ? { min: 0, max: 100 } : {}),
        ...(definition.usesGenericValues ? { value: [] } : {}),
        ...(definition.usesNumericEquation ? { expression: { version: 1 }, comparisonOperator: 'Equal', rightHandSide: { version: 1, root: { nodeType: 'NumericLiteral', value: 0 } } } : {}),
        ...(definition.usesAiTextValidation ? { configuration: { version: 1, targetFields: [], localePolicy: 'AllAvailable', locales: [], prompt: '', contextFields: [], missingTextPolicy: 'Skip', outputLanguage: 'SameAsTarget' } } : {}),
    };
}
